import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, Eye, Copy, Plus, Star, MoreVertical, Archive, Trash2,
  CheckCircle2, FileEdit, Loader2, X, Layers, Sparkles, ChevronRight, Shield,
  ClipboardList, BookOpen, GraduationCap
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RegistroAuxiliarPlantillasTab({ user, token, schoolId, subdomain }) {
  const navigate = useNavigate();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewPlantilla, setPreviewPlantilla] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [usage, setUsage] = useState([]);
  const [usageLoading, setUsageLoading] = useState(true);
  const [activeSubtab, setActiveSubtab] = useState("plantillas");
  const [editTextsOpen, setEditTextsOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadPlantillas = async () => {
    try {
      const { data } = await axios.get(`${API}/api/schools/${schoolId}/registro-auxiliar/plantillas?estado=todas`, { headers });
      setPlantillas(data.plantillas || []);
    } catch (err) {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  };

  const loadUsage = async () => {
    try {
      const { data } = await axios.get(`${API}/api/schools/${schoolId}/registro-auxiliar/usage`, { headers });
      setUsage(data.usage || []);
    } catch {
      // silent — usage card will just not show
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    loadPlantillas();
    loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClone = async (plantillaId, nombre) => {
    try {
      const { data } = await axios.post(`${API}/api/schools/${schoolId}/registro-auxiliar/plantillas/${plantillaId}/clonar`,
        { nombre: `Copia de ${nombre}` }, { headers });
      toast.success("Plantilla clonada — abriendo editor");
      navigate(`/${subdomain}/settings/registro-auxiliar/editor/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al clonar");
    }
  };

  const handleCreate = () => {
    navigate(`/${subdomain}/settings/registro-auxiliar/nueva`);
  };

  const handleEdit = (id) => {
    navigate(`/${subdomain}/settings/registro-auxiliar/editor/${id}`);
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.patch(`${API}/api/schools/${schoolId}/registro-auxiliar/plantillas/${id}/predeterminada`, {}, { headers });
      toast.success("Plantilla marcada como predeterminada");
      loadPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleChangeEstado = async (id, estado) => {
    try {
      await axios.patch(`${API}/api/schools/${schoolId}/registro-auxiliar/plantillas/${id}/estado`, { estado }, { headers });
      toast.success(`Estado cambiado a '${estado}'`);
      loadPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla permanentemente?")) return;
    try {
      await axios.delete(`${API}/api/schools/${schoolId}/registro-auxiliar/plantillas/${id}`, { headers });
      toast.success("Plantilla eliminada");
      loadPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleUseSystem = async () => {
    if (!window.confirm("¿Seguro que quieres desactivar tu plantilla y volver a usar la plantilla del sistema? Los docentes verán las columnas del sistema al registrar notas.")) return;
    try {
      const { data } = await axios.post(`${API}/api/schools/${schoolId}/registro-auxiliar/plantillas/usar-sistema`, {}, { headers });
      toast.success(data.message || "Ahora se usa la plantilla del sistema");
      loadPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar a la plantilla del sistema");
    }
  };

  const sistema = plantillas.find(p => p.es_sistema);
  const propias = plantillas.filter(p => !p.es_sistema);
  const activas = propias.filter(p => p.estado === "activa").length;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6" data-testid="ra-plantillas-tab">
      {/* Subtabs switcher */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit" data-testid="ra-subtabs">
        <button
          type="button"
          onClick={() => setActiveSubtab("plantillas")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubtab === "plantillas"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="ra-subtab-plantillas"
        >
          <Layers className="w-4 h-4" /> Plantillas
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">{plantillas.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveSubtab("usage")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubtab === "usage"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="ra-subtab-usage"
        >
          <ClipboardList className="w-4 h-4" /> Cursos con notas
          {!usageLoading && usage.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">{usage.length}</span>
          )}
        </button>
      </div>

      {/* Subtab: Plantillas */}
      {activeSubtab === "plantillas" && (
      <div className="space-y-8">
      {/* Hero header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Plantillas de Registro Auxiliar</h2>
            <p className="text-sm text-slate-500 mt-0.5">Define la estructura de evaluación que usaran tus docentes al registrar notas.</p>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-xs font-semibold text-slate-400">{propias.length} plantilla{propias.length !== 1 ? "s" : ""} creada{propias.length !== 1 ? "s" : ""}</span>
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              <span className="text-xs font-semibold text-emerald-600">{activas} activa{activas !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
        <button onClick={handleCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all hover:shadow-indigo-500/30 shrink-0"
          data-testid="ra-create-new-btn">
          <Plus className="w-4 h-4" /> Nueva plantilla
        </button>
      </div>

      {/* System Template — Premium card */}
      {sistema && (
        <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white" data-testid="ra-system-template">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-50 via-white to-indigo-50/30" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/[0.03] rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative px-6 py-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  <Shield className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800 text-base">{sistema.nombre}</h3>
                    <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold tracking-wide border border-slate-200">SOLO LECTURA</span>
                    {activas === 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold tracking-wide border border-emerald-200" data-testid="ra-system-in-use-badge">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> EN USO
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1 max-w-lg">{sistema.descripcion || "Estructura de evaluación estandar de EduNet. Clonala para crear tu propia versión personalizada."}</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setPreviewPlantilla(sistema)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm"
                  data-testid="ra-view-system">
                  <Eye className="w-3.5 h-3.5" /> Vista previa
                </button>
                <button onClick={() => setEditTextsOpen(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors shadow-sm"
                  data-testid="ra-edit-system-texts">
                  <FileEdit className="w-3.5 h-3.5" /> Editar textos
                </button>
                {activas > 0 && (
                  <button onClick={handleUseSystem}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors shadow-sm"
                    data-testid="ra-use-system-btn"
                    title="Desactiva tu plantilla y vuelve a usar la plantilla del sistema">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Usar esta plantilla
                  </button>
                )}
                <button onClick={() => handleClone(sistema.id, sistema.nombre)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-500/20"
                  data-testid="ra-clone-system">
                  <Copy className="w-3.5 h-3.5" /> Clonar y personalizar
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pl-[52px]">
              {(sistema.criterios || []).map(c => (
                <span key={c.id} className="text-[10px] px-3 py-1 rounded-lg font-bold text-white shadow-sm" style={{ backgroundColor: c.color || "#94a3b8" }}>
                  {c.nombre} {c.porcentaje}%
                </span>
              ))}
              {(sistema.columnas_finales || []).map(c => (
                <span key={c.id} className="text-[10px] px-3 py-1 rounded-lg font-bold bg-amber-50 text-amber-700 border border-amber-200">
                  {c.label_corto || c.label} {c.porcentaje}%
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* My Templates */}
      {propias.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-wider">Mis Plantillas</h3>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {propias.map(p => (
              <TemplateCard
                key={p.id}
                plantilla={p}
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                onEdit={handleEdit}
                onClone={handleClone}
                onSetDefault={handleSetDefault}
                onChangeEstado={handleChangeEstado}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {propias.length === 0 && (
        <div className="text-center py-16 px-6">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-indigo-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-1">Crea tu primera plantilla</h3>
          <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">Clona la plantilla del sistema o crea una desde cero para definir los criterios de evaluación de tu colegio.</p>
          <div className="flex items-center justify-center gap-3">
            {sistema && (
              <button onClick={() => handleClone(sistema.id, sistema.nombre)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors">
                <Copy className="w-4 h-4" /> Clonar plantilla del sistema
              </button>
            )}
            <button onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm">
              <Plus className="w-4 h-4" /> Crear desde cero
            </button>
          </div>
        </div>
      )}
      </div>
      )}

      {/* Subtab: Cursos con notas */}
      {activeSubtab === "usage" && (
        <div className="space-y-6" data-testid="ra-usage-tab">
          {/* Hero */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Cursos con notas registradas</h2>
              <p className="text-sm text-slate-500 mt-0.5">Asignaturas que ya tienen notas guardadas en este colegio.</p>
            </div>
          </div>

          {usageLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          )}

          {!usageLoading && usage.length === 0 && (
            <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-slate-200 bg-white">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-bold text-slate-700 mb-1">Aún no hay notas registradas</h3>
              <p className="text-sm text-slate-400 max-w-md mx-auto">Cuando los docentes empiecen a registrar notas, verás aquí las asignaturas y secciones con datos.</p>
            </div>
          )}

          {!usageLoading && usage.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="ra-usage-card">
              <div className="divide-y divide-slate-100">
                {usage.map((u) => (
                  <div
                    key={`${u.subject_id}-${u.section_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50/60 transition-colors"
                    data-testid={`ra-usage-row-${u.subject_id}-${u.section_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <BookOpen className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">{u.subject_name}</p>
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                          <GraduationCap className="w-3 h-3 text-slate-400" />
                          <span>{u.level_name}</span>
                          <span className="text-slate-300">•</span>
                          <span>{u.grade_name}</span>
                          <span className="text-slate-300">•</span>
                          <span>Sección {u.section_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-bold">
                        {u.records_count} alumno{u.records_count !== 1 ? "s" : ""}
                      </span>
                      {u.has_dynamic && (
                        <span className="text-[10px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-bold border border-indigo-200" title="Usa columnas de plantilla personalizada">
                          Personalizada
                        </span>
                      )}
                      {!u.has_dynamic && u.has_static && (
                        <span className="text-[10px] px-2 py-1 rounded-full bg-slate-50 text-slate-600 font-bold border border-slate-200" title="Usa columnas de la plantilla del sistema">
                          Sistema
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Texts Editor Modal */}
      {editTextsOpen && sistema && (
        <SystemTextsEditorModal
          sistema={sistema}
          schoolId={schoolId}
          token={token}
          onClose={() => setEditTextsOpen(false)}
          onSaved={() => { setEditTextsOpen(false); loadPlantillas(); }}
        />
      )}

      {/* Preview Modal */}
      {previewPlantilla && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setPreviewPlantilla(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden" data-testid="ra-preview-modal">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold">{previewPlantilla.nombre}</h2>
                <p className="text-indigo-200 text-xs">{previewPlantilla.descripcion || "Vista previa de la estructura"}</p>
              </div>
              <button onClick={() => setPreviewPlantilla(null)} className="p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(85vh-80px)]">
              <PreviewTable config={previewPlantilla} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Template Card ── */
function TemplateCard({ plantilla: p, menuOpen, setMenuOpen, onEdit, onClone, onSetDefault, onChangeEstado, onDelete }) {
  const estadoStyles = {
    activa: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Activa" },
    borrador: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-400", label: "Borrador" },
    archivada: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", dot: "bg-slate-400", label: "Archivada" },
  };
  const est = estadoStyles[p.estado] || estadoStyles.borrador;
  const totalCols = (p.columnas_finales || []).length + (p.criterios || []).reduce((s, c) => s + (c.subcolumnas || []).length, 0);
  const pctSum = (p.criterios || []).reduce((s, c) => s + (c.porcentaje || 0), 0) + (p.columnas_finales || []).reduce((s, c) => s + (c.porcentaje || 0), 0);

  return (
    <div className={`group relative bg-white rounded-2xl border hover:shadow-lg transition-all duration-200 overflow-hidden ${p.estado === "activa" ? "border-emerald-200 ring-1 ring-emerald-100" : "border-slate-200 hover:border-slate-300"}`}
      data-testid={`ra-template-${p.id}`}>
      {/* Top accent bar */}
      <div className="h-1" style={{ background: p.estado === "activa" ? "linear-gradient(90deg, #10b981, #059669)" : p.estado === "borrador" ? "linear-gradient(90deg, #f59e0b, #d97706)" : "#e2e8f0" }} />

      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full font-bold ${est.bg} ${est.text} border ${est.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${est.dot}`} />
              {est.label}
            </span>
            {p.es_predeterminada && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-bold bg-amber-50 text-amber-600 border border-amber-200">
                <Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Default
              </span>
            )}
          </div>
          <div className="relative">
            <button onClick={() => setMenuOpen(menuOpen === p.id ? null : p.id)}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100"
              data-testid={`ra-menu-${p.id}`}>
              <MoreVertical className="w-4 h-4" />
            </button>
            {menuOpen === p.id && (
              <div className="absolute right-0 top-8 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-20 w-52" onMouseLeave={() => setMenuOpen(null)}>
                <button onClick={() => { onClone(p.id, p.nombre); setMenuOpen(null); }} className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-slate-50 flex items-center gap-2.5 text-slate-700"><Copy className="w-3.5 h-3.5 text-slate-400" /> Duplicar plantilla</button>
                {p.estado === "activa" && !p.es_predeterminada && (
                  <button onClick={() => { onSetDefault(p.id); setMenuOpen(null); }} className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-slate-50 flex items-center gap-2.5 text-slate-700"><Star className="w-3.5 h-3.5 text-amber-400" /> Marcar como predeterminada</button>
                )}
                {p.estado === "borrador" && (
                  <button onClick={() => { onChangeEstado(p.id, "activa"); setMenuOpen(null); }} className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-slate-50 flex items-center gap-2.5 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> Activar</button>
                )}
                {p.estado === "activa" && (
                  <button onClick={() => { onChangeEstado(p.id, "borrador"); setMenuOpen(null); }} className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-slate-50 flex items-center gap-2.5 text-slate-700"><FileEdit className="w-3.5 h-3.5 text-slate-400" /> Pasar a borrador</button>
                )}
                <div className="my-1 h-px bg-slate-100" />
                {p.estado !== "archivada" && (
                  <button onClick={() => { onChangeEstado(p.id, "archivada"); setMenuOpen(null); }} className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-slate-50 flex items-center gap-2.5 text-slate-400"><Archive className="w-3.5 h-3.5" /> Archivar</button>
                )}
                <button onClick={() => { onDelete(p.id); setMenuOpen(null); }} className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-rose-50 text-rose-500 flex items-center gap-2.5"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
              </div>
            )}
          </div>
        </div>

        {/* Title + meta */}
        <h4 className="font-extrabold text-slate-900 text-sm mb-0.5 cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => onEdit(p.id)}>
          {p.nombre}
        </h4>
        <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-4">
          <span>{(p.criterios || []).length} criterios</span>
          <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
          <span>{totalCols} columnas</span>
          <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
          <span className={pctSum === 100 ? "text-emerald-500 font-bold" : "text-rose-500 font-bold"}>{Math.round(pctSum)}%</span>
        </div>

        {/* Criteria pills */}
        <div className="flex flex-wrap gap-1.5">
          {(p.criterios || []).map(c => (
            <span key={c.id} className="text-[9px] px-2 py-0.5 rounded-md font-bold text-white shadow-sm" style={{ backgroundColor: c.color || "#94a3b8" }}>
              {c.nombre} {c.porcentaje}%
            </span>
          ))}
          {(p.columnas_finales || []).map(c => (
            <span key={c.id} className="text-[9px] px-2 py-0.5 rounded-md font-bold bg-amber-50 text-amber-700 border border-amber-200">
              {c.label_corto || c.label} {c.porcentaje}%
            </span>
          ))}
        </div>

        {/* Footer action */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[10px] text-slate-400">
            {p.updated_at ? `Editado ${new Date(p.updated_at).toLocaleDateString("es-PE", { day: "numeric", month: "short" })}` : ""}
          </span>
          <button onClick={() => onEdit(p.id)}
            className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
            data-testid={`ra-edit-${p.id}`}>
            Editar <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Preview Table ── */
function PreviewTable({ config }) {
  const criterios = config.criterios || [];
  const columnas = config.columnas_finales || [];
  const students = ["Garcia Lopez, Ana Maria", "Mendoza Torres, Carlos", "Quispe Huamani, Lucia"];

  return (
    <div className="overflow-x-auto border border-slate-200 rounded-xl">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th rowSpan={2} className="bg-slate-100 text-slate-600 font-bold px-3 py-2 border border-slate-200 min-w-[150px] text-left sticky left-0 z-10">ALUMNO</th>
            {criterios.map(c => (
              <th key={c.id} colSpan={(c.subcolumnas || []).length} className="text-white font-bold px-2 py-2 border border-white/20 text-center" style={{ backgroundColor: c.color || "#94a3b8" }}>
                {c.nombre} {c.porcentaje}%
              </th>
            ))}
            {columnas.map(col => (
              <th key={col.id} rowSpan={2} className="bg-amber-400 text-white font-bold px-2 py-2 border border-amber-300 text-center min-w-[50px]">
                {col.label_corto || col.label} {col.porcentaje}%
              </th>
            ))}
            <th rowSpan={2} className="bg-emerald-600 text-white font-bold px-3 py-2 border border-emerald-500 text-center min-w-[80px]">
              {config.label_promedio_final || "PROM."}
            </th>
          </tr>
          <tr>
            {criterios.flatMap(c => (c.subcolumnas || []).map(s => (
              <th key={s.id} className="bg-slate-50 text-slate-600 font-semibold px-1 py-1.5 border border-slate-200 text-center min-w-[35px]">
                {s.label}
              </th>
            )))}
          </tr>
        </thead>
        <tbody>
          {students.map((name, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="px-3 py-2 border border-slate-200 font-medium text-slate-700 sticky left-0 bg-white z-10">{name}</td>
              {criterios.flatMap(c => (c.subcolumnas || []).map(s => (
                <td key={s.id} className={`px-1 py-2 border border-slate-200 text-center ${s.tipo === "promedio_auto" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-400"}`}>
                  {s.tipo === "promedio_auto" ? "—" : ""}
                </td>
              )))}
              {columnas.map(col => (
                <td key={col.id} className="px-1 py-2 border border-slate-200 text-center text-slate-400"></td>
              ))}
              <td className="px-2 py-2 border border-slate-200 text-center bg-emerald-50 text-emerald-700 font-bold">—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


/* ── System Texts Editor Modal ── */
function SystemTextsEditorModal({ sistema, schoolId, token, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    nombre: sistema.nombre || "",
    descripcion: sistema.descripcion || "",
    label_promedio_final: sistema.label_promedio_final || "",
    criterios: (sistema.criterios || []).map(c => ({
      id: c.id,
      nombre: c.nombre || "",
      porcentaje: c.porcentaje,
      color: c.color,
      subcolumnas: (c.subcolumnas || []).map(s => ({
        id: s.id, label: s.label || "", tipo: s.tipo,
      })),
    })),
    columnas_finales: (sistema.columnas_finales || []).map(c => ({
      id: c.id, label: c.label || "", label_corto: c.label_corto || "",
      porcentaje: c.porcentaje,
    })),
  }));
  const [saving, setSaving] = useState(false);

  const updateCriterioNombre = (idx, value) => {
    setForm(prev => {
      const criterios = [...prev.criterios];
      criterios[idx] = { ...criterios[idx], nombre: value };
      return { ...prev, criterios };
    });
  };
  const updateSubLabel = (cIdx, sIdx, value) => {
    setForm(prev => {
      const criterios = [...prev.criterios];
      const subs = [...criterios[cIdx].subcolumnas];
      subs[sIdx] = { ...subs[sIdx], label: value };
      criterios[cIdx] = { ...criterios[cIdx], subcolumnas: subs };
      return { ...prev, criterios };
    });
  };
  const updateColumnaFinal = (idx, field, value) => {
    setForm(prev => {
      const columnas = [...prev.columnas_finales];
      columnas[idx] = { ...columnas[idx], [field]: value };
      return { ...prev, columnas_finales: columnas };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        label_promedio_final: form.label_promedio_final,
        criterios: form.criterios.map(c => ({
          id: c.id,
          nombre: c.nombre,
          subcolumnas: c.subcolumnas.map(s => ({ id: s.id, label: s.label })),
        })),
        columnas_finales: form.columnas_finales.map(c => ({
          id: c.id, label: c.label, label_corto: c.label_corto,
        })),
      };
      await axios.patch(
        `${API}/api/schools/${schoolId}/registro-auxiliar/plantillas/system/textos`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Textos actualizados correctamente");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="ra-edit-system-texts-modal">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <FileEdit className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold">Editar textos de la plantilla del sistema</h2>
              <p className="text-amber-50 text-xs">Solo puedes cambiar los nombres y labels. Los porcentajes y la estructura se mantienen.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/20" data-testid="ra-edit-system-texts-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* General */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Nombre de la plantilla</label>
              <input type="text" value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white outline-none transition-all"
                data-testid="ra-edit-system-nombre" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Label del promedio final</label>
              <input type="text" value={form.label_promedio_final}
                onChange={(e) => setForm({ ...form, label_promedio_final: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white outline-none transition-all"
                data-testid="ra-edit-system-label-final" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Descripción</label>
              <input type="text" value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white outline-none transition-all"
                data-testid="ra-edit-system-descripcion" />
            </div>
          </div>

          {/* Criterios */}
          <div>
            <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-wider mb-3">Criterios</h3>
            <div className="space-y-4">
              {form.criterios.map((c, cIdx) => (
                <div key={c.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid={`ra-edit-criterio-${c.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] px-2.5 py-1 rounded-lg font-bold text-white shadow-sm" style={{ backgroundColor: c.color || "#94a3b8" }}>
                      {c.porcentaje}%
                    </span>
                    <input type="text" value={c.nombre}
                      onChange={(e) => updateCriterioNombre(cIdx, e.target.value)}
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                      placeholder="Nombre del criterio"
                      data-testid={`ra-edit-criterio-nombre-${c.id}`} />
                  </div>
                  {c.subcolumnas.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {c.subcolumnas.map((s, sIdx) => (
                        <div key={s.id} className="flex flex-col gap-1">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase">{s.tipo === "promedio_auto" ? "Promedio" : "Subcolumna"}</span>
                          <input type="text" value={s.label}
                            onChange={(e) => updateSubLabel(cIdx, sIdx, e.target.value)}
                            className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                            data-testid={`ra-edit-sub-${s.id}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Columnas finales */}
          {form.columnas_finales.length > 0 && (
            <div>
              <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-wider mb-3">Columnas finales</h3>
              <div className="space-y-3">
                {form.columnas_finales.map((c, idx) => (
                  <div key={c.id} className="bg-amber-50 rounded-xl p-4 border border-amber-200 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center" data-testid={`ra-edit-columna-${c.id}`}>
                    <span className="text-[10px] px-2.5 py-1 rounded-lg font-bold bg-amber-200 text-amber-800 sm:col-span-1 text-center">{c.porcentaje}%</span>
                    <div className="sm:col-span-7">
                      <label className="block text-[10px] font-bold text-amber-700 uppercase mb-1">Label largo</label>
                      <input type="text" value={c.label}
                        onChange={(e) => updateColumnaFinal(idx, "label", e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                        data-testid={`ra-edit-col-label-${c.id}`} />
                    </div>
                    <div className="sm:col-span-4">
                      <label className="block text-[10px] font-bold text-amber-700 uppercase mb-1">Label corto</label>
                      <input type="text" value={c.label_corto}
                        onChange={(e) => updateColumnaFinal(idx, "label_corto", e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                        data-testid={`ra-edit-col-short-${c.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50 shrink-0">
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
            data-testid="ra-edit-system-cancel">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-2 shadow-sm"
            data-testid="ra-edit-system-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}
